import React, { useContext, useState } from 'react'
import { space } from '@entur/tokens'
import { Badge } from '@entur/layout'
import { AddIcon } from '@entur/icons'

import { FetchState } from '../../../api-clients/api-types'
import { useCategory, useDisabilityProof, useFeePaid } from '../../../hooks/employee-yearly-data-hooks'
import { useEmployee } from '../../../contexts/employee-context'
import CategoryDropdown, { categoryFormField } from '../../form/category-dropdown'
import { useGenerateTicketRights } from '../../../hooks/use-ticket-rights'

import { ChangeTicketRightModal } from '../change-ticket-right-modal'
import { CardActionChip, TicketRightCardBase } from './ticket-right-card-base'
import { RuleElement } from '../rule-element'
import { TicketRightWithDraft } from '../../../types'
import { ChangedTicketRightDraft } from '../drafts/changed-ticket-right-draft'
import { useClientInfo } from '../../../contexts/client-info-context'
import { ReadOnlyContext } from '../../../contexts/read-only-context'

type YearlyTicketRightCardProps = {
  ticketRightWithDraft: TicketRightWithDraft
}
export const YearlyTicketRightCard: React.FC<YearlyTicketRightCardProps> = ({ ticketRightWithDraft }) => {
  const { ticketRight, draft } = ticketRightWithDraft
  const { hasSensitiveAccess } = useClientInfo()
  const { readOnly } = useContext(ReadOnlyContext)
  const { employee } = useEmployee()
  const year = parseInt(ticketRight.validFrom.split('-')[0], 10)
  const [isOpenBlueModal, setOpenBlueModal] = useState<boolean>(false)

  const [generateTicketRightsResponse, generateTicketRights] = useGenerateTicketRights(() =>
    setOpenBlueModal(false)
  )
  const [addFeeResponse, setFeePaid] = useFeePaid(year, () => generateTicketRights())
  const [addDisabilityProofResponse, setDisabilityProof] = useDisabilityProof(year, () => generateTicketRights())
  const [setCategoryResponse, setCategory] = useCategory(year, () => generateTicketRights())

  const isCategoryLoading =
    setCategoryResponse.status === FetchState.LOADING || generateTicketRightsResponse.status === FetchState.LOADING
  const disabilityProofRegistered = employee.sensitiveInfo?.disabilityProofs?.find(
    (it) => it.year === year
  )?.isRegistered
  const isFeePaid = employee.yearlyFees.find((it) => it.year === year)?.isPaid
  const category = employee.categories.find((it) => it.year === year)?.category
  const [selectedCategory, setSelectedCategory] = useState(categoryFormField(category))
  const categoryName =
    ticketRight?.ticketType?.description?.split(' ')[3]?.trim() || category?.name || 'Ikke valgt'

  let errorMessage
  if (generateTicketRightsResponse.status === FetchState.ERROR) {
    errorMessage = 'Generering av ny rettighet feilet på grunn av teknisk feil'
  }
  if (addFeeResponse.status === FetchState.ERROR) {
    errorMessage = 'Lagring av egenandel feilet på grunn av teknisk feil'
  }
  if (addDisabilityProofResponse.status === FetchState.ERROR) {
    errorMessage = 'Lagring av uføreattest feilet på grunn av teknisk feil'
  }
  if (setCategoryResponse.status === FetchState.ERROR) {
    errorMessage = 'Lagring av kategori feilet på grunn av teknisk feil'
  }

  const badges =
    disabilityProofRegistered || isFeePaid ? (
      <>
        {disabilityProofRegistered && (
          <Badge variant="neutral" style={{ float: 'left', marginRight: space.extraSmall }}>
            Uføreattest
          </Badge>
        )}
        {isFeePaid && (
          <Badge variant="neutral" style={{ float: 'left' }}>
            Egenandel
          </Badge>
        )}
      </>
    ) : undefined

  const selectCategoryDropdown = (
    <ChangeTicketRightModal
      title="Endre kategori"
      onSubmit={() => setCategory(selectedCategory.value)}
      isOpen={isOpenBlueModal}
      setOpen={() => setOpenBlueModal(false)}
      isLoading={isCategoryLoading}
      errorMessage={errorMessage}
    >
      <CategoryDropdown category={selectedCategory} company={employee.company} update={setSelectedCategory} />
    </ChangeTicketRightModal>
  )

  const actionChips =
    !readOnly && hasSensitiveAccess === true ? (
      <>
        <CardActionChip onClick={() => setOpenBlueModal(true)}>Endre kategori</CardActionChip>
        {isOpenBlueModal && selectCategoryDropdown}
        {!disabilityProofRegistered && (
          <CardActionChip onClick={() => setDisabilityProof(true)}>
            <AddIcon />
            Uføreattest
          </CardActionChip>
        )}
        {!isFeePaid && (
          <CardActionChip onClick={() => setFeePaid(true)}>
            <AddIcon />
            Egenandel
          </CardActionChip>
        )}
      </>
    ) : (
      <></>
    )

  return (
    <>
      {draft && <ChangedTicketRightDraft activeTicketRight={ticketRight} draft={draft} />}
      <TicketRightCardBase
        ticketRight={ticketRight}
        actionChips={actionChips}
        badges={badges}
        errorMessage={errorMessage}
        categoryName={categoryName}
        disabilityProofRegistered={disabilityProofRegistered}
        isFeePaid={isFeePaid}
      >
        <RuleElement createdByRule={ticketRight?.createdByRule} />
      </TicketRightCardBase>
    </>
  )
}
